import type { NavigationProp, ParamListBase } from "@react-navigation/core";
import React from "react";
import { Pressable, StyleProp, StyleSheet, TextStyle, TouchableOpacity, View } from "react-native";
import { useSelector } from "react-redux";
import { kColors } from "../kColors";
import { kStyleConst } from "../kStyle";
import { kRoute } from "../kRoute";

import * as connectionDb from "src/db/connection.db";
import { includesIn } from "src/util/search";
import { PdCopy, PdPencilSquare, PdStorefront, PdTrash } from "src/components/Icons";
import { useAppDispatch } from "src/redux";
import { PdStack, PdText } from "src/components/layout";
import {
  PdTable,
  PdTableCell,
  PdTableCellText,
  PdTableRow,
  PdTableSortableHeaders,
} from "src/components/v2/layout/table";
import { tPressableState } from "src/components/types";
import { PdSearchInput } from "src/components/v2/ui";
import { tColumn, tRowInstance, tTableSortDirection, useTable } from "src/hooks/table";
import { useIsMobile, useUserProfile } from "src/hooks/ui";
import { getHiddenColumnsFromConfig } from "src/util/table";
import { formatMoney, formatPercentage } from "src/util/gFormatting";
import { copyLinkToClipboard } from "src/util/functions";
import { PdIconButton } from "src/components/forms/icon-button";
import api from "src/util/api";
import { useAddRetailerModal, useConfirmModal } from "src/hooks/modals";
import { PdButton, PdButtonLabel } from "src/components/v2/forms/button";
import { PdFormLabel } from "src/components/v2/forms/control";

type tTableDraft = {
  id: string;
  dateCreated: number;
  draftTitle?: string;
  promoCode?: string;
  discountCost?: number;
  discountPercent?: number;
  draftLink?: string;
  subtotal: number;
};

type tFilters = {
  term: string;
  sortedBy: keyof tTableDraft;
  sortDirection: tTableSortDirection;
};

const formatDate = (dateInMilliseconds: number) => new Date(dateInMilliseconds).toLocaleDateString("en-US");

const useTableData = (filters: tFilters) => {
  const rDrafts = useSelector((state) => state.DraftOrderReducer.drafts);
  return React.useMemo(() => {
    const data: tTableDraft[] = [];
    for (const draft of rDrafts) {
      const row: tTableDraft = {
        id: draft.draftCartId ?? "",
        dateCreated: draft.dateCreated ? draft.dateCreated.seconds * 1000 : 0,
        draftTitle: draft.draftTitle,
        promoCode: draft.discountCode,
        discountCost: draft.discountCost,
        discountPercent: draft.discountPercent,
        draftLink: draft.draftLink,
        subtotal: draft.totalPrice,
      };
      const termFilter = !filters.term || includesIn(row, filters.term);
      if (termFilter) {
        data.push(row);
      }
    }
    return data;
  }, [filters.term, rDrafts]);
};

const LinkCell: React.FC<{ link?: string; title?: string }> = ({ link, title }) => {
  const dispatch = useAppDispatch();

  const onPress = () => {
    if (link) {
      copyLinkToClipboard(link, title);
      dispatch({ type: "RIBBON", message: "Copied link to clipboard" });
    }
  };

  return (
    <TouchableOpacity onPress={onPress} style={[styles.flexRow, styles.alignCenter]}>
      <PdText>Get Link</PdText>
      <PdCopy style={styles.copyLinkIcon} color={kColors.brandDark} />
    </TouchableOpacity>
  );
};

const RemoveCell: React.FC<{ id: string; style?: StyleProp<TextStyle> }> = ({ id, style }) => {
  const confirmModal = useConfirmModal();

  const handleOnPress = async () => {
    const response = await confirmModal.openAsync({
      title: "Delete Draft Order",
      description: "Are you sure you want to delete this draft order?",
      confirmButtonLabel: "Confirm",
    });
    if (response.status === "closed") return;

    api.post({ endpoint: `/cart/${id}/deleteDraft`, body: undefined });
  };

  return (
    <PdIconButton
      contentContainerStyle={styles.removeButton}
      onPress={handleOnPress}
      size="sm"
      variant="ghost"
      icon={<PdTrash style={styles.trashIcon} />}
      style={style}
    />
  );
};

const useTableColumns = () => {
  return React.useMemo(() => {
    const columns: tColumn<tTableDraft>[] = [
      {
        id: "dateCreated",
        header: "Date",
        accessor: (originalRow) => (originalRow.dateCreated ? formatDate(originalRow.dateCreated) : "-"),
        Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
        style: styles.tableColumnMd,
      },
      {
        id: "draftTitle",
        header: "Title",
        accessor: (originalRow) => originalRow.draftTitle || "-",
        Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
        style: styles.tableColumnLg,
      },
      {
        id: "promoCode",
        header: "Promo Code",
        accessor: (originalRow) => originalRow.promoCode || "-",
        Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
        style: styles.tableColumnMd,
      },
      {
        id: "discount",
        header: "Discount",
        accessor: (originalRow) =>
          originalRow.discountPercent
            ? formatPercentage(originalRow.discountPercent)
            : originalRow.discountCost
            ? formatMoney(originalRow.discountCost)
            : "-",
        Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
        style: styles.tableColumnMd,
      },
      {
        id: "draftLink",
        header: "Link",
        accessor: (originalRow) => originalRow.draftLink,
        Cell: ({ row }) => <LinkCell link={row.draftLink} title={row.draftTitle} />,
        isSortable: false,
        style: styles.tableColumnMd,
      },
      {
        id: "subtotal",
        header: "Subtotal",
        accessor: (originalRow) => (originalRow.subtotal ? formatMoney(originalRow.subtotal) : "-"),
        Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
        style: styles.tableColumnMd,
      },
      {
        id: "remove-button",
        header: "Remove",
        accessor: (originalRow) => originalRow.id,
        Cell: ({ row, style }) => <RemoveCell id={row.id} style={style} />,
        isSortable: false,
        style: { width: 50, alignItems: "center", justifyContent: "center" },
      },
    ];
    return columns;
  }, []);
};

const P03_02_AllDrafts: React.FC<{ navigation: NavigationProp<ParamListBase> }> = ({ navigation }) => {
  const { isAdmin } = useUserProfile();
  const rCompanyType = useSelector((state) => state.CompanyReducer.type);
  const rCompanyId = useSelector((state) =>
    state.CompanyReducer.type === "brand" || state.CompanyReducer.type === "rep" ? state.CompanyReducer.id : "",
  );
  const rBrandName = useSelector((state) => (state.CompanyReducer.type === "brand" ? state.CompanyReducer.name : ""));
  const rBrandDefaultShipping = useSelector((state) =>
    state.CompanyReducer.type === "brand" ? state.CompanyReducer.defaultShipPercent : undefined,
  );
  const rTableConfigs = useSelector((state) => state.TableConfigReducer.applications);
  const [filters, setFilters] = React.useState<tFilters>({
    term: "",
    sortedBy: "dateCreated",
    sortDirection: "desc",
  });
  const data = useTableData(filters);
  const columns = useTableColumns();
  const tableInstance = useTable<tTableDraft>({
    data,
    columns,
    initialState: {
      sortedBy: filters.sortedBy,
      sortDirection: filters.sortDirection,
      hiddenColumns: getHiddenColumnsFromConfig(rTableConfigs),
    },
  });
  const isMobile = useIsMobile();
  const addRetailerModal = useAddRetailerModal();

  const handleOnCreateOrder = async () => {
    if (rCompanyType === "brand") {
      navigation.navigate(kRoute.P100, { brand: rBrandName });
    } else if (rCompanyType === "rep") {
      const connectionsQuery = await connectionDb.queryBrandRepConnection({ repId: rCompanyId }).get();

      if (connectionsQuery.size > 1) {
        navigation.navigate(kRoute.P99);
      } else if (connectionsQuery.docs[0]) {
        navigation.navigate(kRoute.P100, { brand: connectionsQuery.docs[0].data().business.name });
      }
    }
  };

  const handleOnPressAddRetailer = () => {
    addRetailerModal.openAsync({
      companyId: rCompanyId,
      brandDefaultShippingFee: rBrandDefaultShipping,
      source: "brand",
    });
  };

  const handleOnRowPress = (item: tRowInstance<tTableDraft>) => {
    navigation.navigate(kRoute.P103, { id: item.id });
  };

  return (
    <View style={isMobile ? styles.mobilePage : styles.page}>
      <PdStack direction={isMobile ? "vertical" : "horizontal"} style={[styles.justifyBetween, styles.mb4]}>
        <PdText variant="subheader">Created Orders</PdText>
        <PdStack direction={isMobile ? "vertical" : "horizontal"}>
          {!isAdmin && (
            <PdButton
              onPress={handleOnCreateOrder}
              colorScheme="purple"
              leftIcon={<PdPencilSquare style={styles.pencilSquareIcon} />}
              style={styles.flex1}
            >
              <PdButtonLabel>Create An Order</PdButtonLabel>
            </PdButton>
          )}
          <PdButton onPress={handleOnPressAddRetailer} leftIcon={<PdStorefront style={styles.pencilSquareIcon} />}>
            <PdButtonLabel>Add a Retailer</PdButtonLabel>
          </PdButton>
        </PdStack>
      </PdStack>
      <PdText style={[styles.textGray, styles.mb4]}>
        {rCompanyType === "rep"
          ? "To write an order, simply shop your brands and create a cart. You can then share this cart with any of your retailers, whether they currently carry the brand or not. When a Retailer checks out the order and the brand ships it, your earned commission assigned by the brand will be paid out directly into your bank account."
          : "Here you can easily create an order and share with your retailers via messages. To create an order, simply shop your brand and share the cart with your retailers. Any orders you create that a retailer checks out are commission free! All orders you create will be saved in the Created Orders table, so you can share them again in the future."}
      </PdText>
      <View style={styles.mb4}>
        {!isMobile && <PdFormLabel>Search your created orders</PdFormLabel>}
        <PdSearchInput
          value={filters.term}
          onChangeText={(newTerm) => setFilters((prev) => ({ ...prev, term: newTerm }))}
        />
      </View>
      <PdTable
        ListHeaderComponent={<PdTableSortableHeaders headers={tableInstance.headers} />}
        data={tableInstance.rows}
        keyExtractor={(item) => item.id}
        renderItem={({ item }) => (
          <Pressable onPress={() => handleOnRowPress(item)}>
            {({ pressed, hovered }: tPressableState) => (
              <PdTableRow style={[hovered ? styles.tableRowHovered : {}, pressed ? styles.tableRowPressed : {}]}>
                {item.cells.map((cell) => (
                  <PdTableCell key={cell.uniqueId} style={cell.style}>
                    {cell.render("Cell")}
                  </PdTableCell>
                ))}
              </PdTableRow>
            )}
          </Pressable>
        )}
        key={`${isMobile}`}
      />
    </View>
  );
};

export const P03_02 = React.memo(P03_02_AllDrafts);

const styles = StyleSheet.create({
  alignCenter: {
    alignItems: "center",
  },
  copyLinkIcon: {
    height: 11,
    marginLeft: kStyleConst.kSpacing,
    width: 11,
  },
  flex1: {
    flex: 1,
  },
  flexRow: {
    flexDirection: "row",
  },
  justifyBetween: {
    justifyContent: "space-between",
  },
  mb4: {
    marginBottom: kStyleConst.kSpacing * 4,
  },
  mobilePage: {
    flex: 1,
    paddingHorizontal: kStyleConst.kSpacing * 4,
    paddingVertical: kStyleConst.kSpacing * 4,
  },
  page: {
    flex: 1,
    paddingHorizontal: kStyleConst.kSpacing * 10,
    paddingVertical: kStyleConst.kSpacing * 8,
  },
  pencilSquareIcon: {
    height: 15,
  },
  removeButton: {
    height: 34,
    width: 34,
  },
  tableColumnLg: {
    width: 300,
  },
  tableColumnMd: {
    width: 160,
  },
  tableRowHovered: {
    backgroundColor: kColors.gray50,
  },
  tableRowPressed: {
    opacity: 0.6,
  },
  textGray: {
    color: kColors.gray500,
  },
  trashIcon: {
    width: 14,
  },
});
